/* global window */

import videojs from 'video.js';
import adblockDetect from 'adblock-detect';
import canAutoPlay from 'can-autoplay';
import get from 'lodash/get';
import Cookies from 'js-cookie';
import { init } from '@sentry/browser';

import settings from './settings';
import EncoreInjector from './EncoreInjector';
import {
  catchErrors,
  whenDomReady,
  loadScript,
  checkWebPFeature,
  ignoreEvent,
} from './utils';

/**
 * Check if any sort of ad blocking extension is enabled.
 * @returns {Promise<boolean>} ad block detected.
 */
function detectAdBlock() {
  return new Promise(resolve => {
    adblockDetect(adBlockDetected => {
      return resolve(adBlockDetected);
    });
  });
}

/**
 * Check if video autoplay is allowed.
 * @returns {Promise<boolean>} autoplay allowed.
 */
function detectAutoplay() {
  return new Promise(resolve => {
    canAutoPlay
      .video({
        timeout: 250,
        inline: true,
      })
      .then(({ result, error }) => {
        if (result === false) {
          videojs.log(`canAutoplay: [${error}]`);
        }
        return resolve(result);
      });
  });
}

/**
 * Detect WebP support.
 * @returns {Promise<boolean>} WebP capable.
 */
function detectWebP() {
  return new Promise(resolve => {
    checkWebPFeature('animation', (feature, result) => {
      if (feature === 'animation') {
        return resolve(result);
      }
      return resolve(false);
    });
  });
}

/**
 * Detect Data Privacy/Do Not Sell status.
 * @returns {Promise<boolean>} Do Not Sell status.
 */
function detectDataPrivacy() {
  if (!window.HTV_DP_CF || !window.HTV_DP_CF_URL) {
    return Promise.resolve(false);
  }

  if (typeof window.HTV_DP_CF_DNS === 'boolean') {
    return Promise.resolve(window.HTV_DP_CF_DNS);
  }

  const hdpcid = Cookies.get('_hdpcid');
  if (!hdpcid) {
    return Promise.resolve(false);
  }

  const url = `${window.HTV_DP_CF_URL}?hdpcid=${hdpcid}`;
  return fetch(url)
    .then(response => {
      if (response.status === 200) {
        return response.json();
      }
      return false;
    })
    .then(response => {
      const dp = get(response, 'data.dp', false);
      return dp === 1;
    })
    .catch(error => {
      videojs.log(error);
      return false;
    });
}

/**
 * Bootstrap Sentry error reporting.
 */
function initSentry() {
  if (!settings.sentrySettings.dsn) {
    videojs.log('sentry = [disabled]');
    return;
  }

  // Configure sentry and follow all errors.
  // Use Sentry beforeSend hook to ignore abort errors.
  const beforeSend = (event, hint) => {
    if (ignoreEvent(event, hint)) {
      return false;
    }
    return event;
  };

  init({ ...settings.sentrySettings, beforeSend });
  videojs.log('sentry = [enabled]');
}

/**
 * Kickstart video player. Insert player variable into global context.
 */
function kickstart() {
  initSentry();
  window.hearstPlayer = new EncoreInjector(settings);
  window.hearstPlayer.initialize(window);
}

/**
 * Perform all pre-player initialization tasks.
 * Once finished, kickstart video player.
 * @async
 */
async function bootstrap() {
  const [
    domReady,
    imaSdkLoaded,
    canAutoplay,
    adBlockDetected,
    canUseWebP,
    doNotSellStatus,
  ] = await Promise.all([
    catchErrors(whenDomReady()),
    catchErrors(loadScript(settings.imaSdkUrl)),
    catchErrors(detectAutoplay()),
    catchErrors(detectAdBlock()),
    catchErrors(detectWebP()),
    catchErrors(detectDataPrivacy()),
  ]);

  const imaSdkReady = typeof get(window, 'google.ima') !== 'undefined';
  const imaSdkAvailable = imaSdkLoaded && imaSdkReady;
  settings.imaSdkAvailable = imaSdkAvailable;
  settings.canAutoplay = canAutoplay;
  settings.adBlockDetected = adBlockDetected;
  settings.canUseWebP = canUseWebP;
  settings.doNotSellStatus = doNotSellStatus;

  if (window.HTV_SENTRY_DSN && window.HTV_PLAYER_VERSION) {
    settings.sentrySettings.dsn = window.HTV_SENTRY_DSN;
    settings.sentrySettings.release = window.HTV_PLAYER_VERSION;
  }

  videojs.log(`domReady = [${domReady}]`);
  videojs.log(`imaSdkAvailable = [${imaSdkAvailable}]`);
  videojs.log(`canAutoplay = [${canAutoplay}]`);
  videojs.log(`adBlockDetected = [${adBlockDetected}]`);
  videojs.log(`canUseWebP = [${canUseWebP}]`);
  videojs.log(`doNotSellStatus = [${doNotSellStatus}]`);

  kickstart();
}

export default bootstrap;
